import { Link } from 'react-router-dom';

const experiences = [
  {
    icon: 'ri-file-list-3-line',
    title: 'First-hand Licensing Experience',
    description:
      'We successfully obtained our own ryokan license in Ota City, navigating every step of the application process, document preparation, and inspection requirements. This direct experience means we understand exactly what works and what doesn\'t.',
    stats: { value: '100%', label: 'Success Rate' },
    color: 'from-emerald-500 to-teal-500',
  },
  {
    icon: 'ri-home-smile-line',
    title: 'Actual Airbnb Operation',
    description:
      'We actively operate our own traditional lodging facility in Ota City on Airbnb and other platforms. We manage real bookings, handle guest communications, coordinate cleaning, and optimize pricing daily — giving us practical insights that consultants simply cannot provide.',
    stats: { value: '365', label: 'Days/Year' },
    color: 'from-blue-500 to-cyan-500',
  },
  {
    icon: 'ri-government-line',
    title: 'Local Authority Communication',
    description:
      'Through our own licensing process and ongoing compliance requirements, we\'ve built strong relationships with Ota City officials and understand their expectations. We know which documents they prioritize, how to address their concerns, and how to expedite approvals.',
    stats: { value: '5+', label: 'Years Experience' },
    color: 'from-violet-500 to-purple-500',
  },
  {
    icon: 'ri-line-chart-line',
    title: 'Practical Revenue Insights',
    description:
      'Our revenue optimization strategies are based on real data from our own operations — not theoretical models. We\'ve tested different pricing strategies, seasonal adjustments, and guest targeting approaches, and we know what generates the best returns in Tokyo\'s competitive market.',
    stats: { value: '30%+', label: 'Avg. Revenue Increase' },
    color: 'from-amber-500 to-orange-500',
  },
];

const credentials = [
  { icon: 'ri-shield-check-line', text: 'Licensed Ryokan Operator in Ota City' },
  { icon: 'ri-star-line', text: 'Airbnb Superhost Status' },
  { icon: 'ri-team-line', text: 'Direct Experience with Tokyo Authorities' },
  { icon: 'ri-award-line', text: 'Proven Track Record in Revenue Optimization' },
];

export default function AuthoritySection() {
  return (
    <section className="py-24 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, rgb(148 163 184) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">

        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full mb-4 border border-emerald-200">
            <i className="ri-medal-line text-sm"></i>
            <span className="text-sm font-semibold tracking-wide uppercase">Our Authority</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 leading-tight">
            Real Experience in<br className="hidden md:block" /> Ota City, Tokyo
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            We don't just advise — we actively operate our own licensed lodging facility in Ota City. Our guidance is based on firsthand experience, not theory.
          </p>
        </div>

        {/* Credentials Banner */}
        <div className="mb-16 bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {credentials.map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-emerald-100">
                  <i className={`${item.icon} text-emerald-600 text-lg`}></i>
                </div>
                <span className="text-sm font-semibold text-slate-700 leading-snug">{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Experience Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {experiences.map((exp, i) => (
            <div
              key={i}
              className="group relative bg-white rounded-2xl shadow-lg border border-slate-200 p-8 hover:shadow-2xl transition-all duration-300"
            >
              {/* Icon & Stats */}
              <div className="flex items-start justify-between mb-6">
                <div className={`w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-br ${exp.color} shadow-lg`}>
                  <i className={`${exp.icon} text-white text-2xl`}></i>
                </div>
                <div className="text-right">
                  <div className={`text-3xl font-bold bg-gradient-to-r ${exp.color} bg-clip-text text-transparent`}>
                    {exp.stats.value}
                  </div>
                  <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">
                    {exp.stats.label}
                  </div>
                </div>
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold text-slate-900 mb-3">{exp.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm">{exp.description}</p>

              {/* Hover Effect Border */}
              <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${exp.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300 pointer-events-none`}></div>
            </div>
          ))}
        </div>

        {/* Quote Section */}
        <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-16">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/a3d3d4e68b29a32cebb76f3d64809b22.jpg"
              alt="Professional business owner in Tokyo"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/70"></div>
          </div>

          <div className="relative px-10 py-16 md:px-16 md:py-20">
            <div className="max-w-3xl">
              <div className="mb-6">
                <i className="ri-double-quotes-l text-5xl text-emerald-400 opacity-50"></i>
              </div>
              <blockquote className="text-2xl md:text-3xl font-bold text-white leading-relaxed mb-8">
                "We've walked the exact path our clients are about to take. Every challenge, every inspection, every guest interaction — we've experienced it ourselves. That's why our advice works."
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-emerald-500">
                  <i className="ri-user-line text-white text-xl"></i>
                </div>
                <div>
                  <div className="text-white font-semibold">Magome Kominka Team</div>
                  <div className="text-emerald-300 text-sm">Active Operators &amp; Consultants</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Why It Matters */}
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-10 md:p-12 border-2 border-emerald-200">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-slate-900 mb-3">
              Why Real Experience Matters
            </h3>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              The difference between theoretical knowledge and practical expertise is the difference between success and struggle.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {[
              {
                title: 'We Know What Works',
                text: 'Our strategies are tested in real operations, not borrowed from textbooks.',
              },
              {
                title: 'We Understand Challenges',
                text: 'We\'ve faced the same obstacles you will — and we know how to overcome them.',
              },
              {
                title: 'We Stay Current',
                text: 'As active operators, we stay updated on regulation changes and market trends daily.',
              },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-emerald-100">
                <h4 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h4>
                <p className="text-slate-600 text-sm leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center bg-white p-8 md:p-12 rounded-lg border-2 border-[#B8860B]/30 shadow-lg">
            <h3 className="text-2xl md:text-3xl font-bold text-[#8B4513] mb-4">
              Work with Experienced Operators
            </h3>
            <p className="text-gray-700 mb-8 max-w-2xl mx-auto leading-relaxed">
              Benefit from our hands-on experience in <Link to="/tokyo-airbnb-support" className="text-[#B8860B] hover:text-[#8B4513] font-semibold underline">Ota City short-term rental support</Link> and <Link to="/minpaku-license-support" className="text-[#B8860B] hover:text-[#8B4513] font-semibold underline">ryokan license application Tokyo</Link>. We guide you through every step with practical insights from real operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/tokyo-airbnb-support"
                className="px-8 py-4 bg-gradient-to-r from-[#8B4513] to-[#A0522D] text-white font-semibold rounded-lg hover:shadow-xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
              >
                Explore Our Services
              </Link>
              <Link
                to="/airbnb-management-tokyo"
                className="px-8 py-4 bg-white text-[#8B4513] font-semibold rounded-lg border-2 border-[#B8860B] hover:bg-[#F5F1E8] hover:shadow-lg transition-all duration-300 whitespace-nowrap"
              >
                View Pricing Plans
              </Link>
            </div>
          </div>

        </div>

      </div>
    </section>
  );
}